/**
 * Purpose: Tax releated selectors.
 */

import type {
    AcceleronTaxItems,
    Offer,
    OfferType,
    paymentServicesTypes,
    RegionBreakdown,
    TaxBreakdown
} from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { TaxNames } from '@makemydeal/dr-platform-types';
import { adjustTaxItemRates, isFlatTax } from '../utils/taxUtils';
import { getTotalFee } from './feesSelectors';
import {
    getCurrentOfferType,
    getOfferDetails,
    getOfferTerm,
    getSelectedTerm,
    getTaxItemsWithOverrides
} from './offerNodeSelectors';
import { shouldIncludeTaxes } from './paymentSelectors';
import { getTaxItems } from './topLevelSelectors';
import { getFlatTaxItem, getPrimaryTaxItem, getTaxCapped } from './utils/nonSelectorUtils';

export function getTotalTax(state: OfferReduxBaseState, offerType?: OfferType): number {
    const term = getOfferTerm(state, offerType);
    return term.totalTax || 0;
}

export function getAllTaxesItemized(state: OfferReduxBaseState, offerType?: OfferType) {
    const term = getOfferTerm(state, offerType);
    return term.taxItems || [];
}

export function getAllTaxesItemizedWithoutFlat(state: OfferReduxBaseState, offerType?: OfferType) {
    const itemizedTaxes = getAllTaxesItemized(state, offerType);
    return itemizedTaxes.filter((tax) => !isFlatTax(tax));
}

export const getOfferFlatTaxItem = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const taxItems = getAllTaxesItemized(state, offerType);
    const flatTax = getFlatTaxItem(taxItems);
    return flatTax;
};

export const getOfferPrimaryTaxItem = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const taxItems = getAllTaxesItemized(state, offerType);
    const primaryTax = getPrimaryTaxItem(taxItems);
    return primaryTax;
};

export const getManualTotalTax = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, type);
    return selectedOfferDetails.manualTotalTax;
};

export const getManualTotalTaxEdited = (state: OfferReduxBaseState): boolean | undefined => {
    const offerType = getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, offerType);
    return selectedOfferDetails.manualTotalTaxEdited;
};

export const hasManualTotalTax = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const manualTotalTax = getManualTotalTax(state, offerType);
    return manualTotalTax != null;
};

export const getTaxOverrideUpfront = (state: OfferReduxBaseState, offerType?: OfferType): boolean | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, type);
    return selectedOfferDetails.taxOverrideUpfront;
};

export const getTaxItemsWithOverridesWithoutFlat = (state: OfferReduxBaseState, offerType?: OfferType): AcceleronTaxItems => {
    const taxItems = getTaxItemsWithOverrides(state, offerType);
    return taxItems.filter((tax) => !isFlatTax(tax));
};

export const getTaxItemsWithAdjustedRates = (state: OfferReduxBaseState, offerType?: OfferType): AcceleronTaxItems => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state, offerType);
    const enablePercentTaxRates = Boolean(state.featureToggles?.['US2131266_Enable_Percent_Tax_Rates']);
    return adjustTaxItemRates(taxItems, enablePercentTaxRates);
};

export const getManualTaxItems = (
    state: OfferReduxBaseState,
    enableOverridesForSV: boolean = false,
    offerType?: OfferType
): AcceleronTaxItems => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state, offerType);
    return taxItems
        .filter((taxItem) => taxItem.isManualTax)
        .filter((taxItem) => !enableOverridesForSV || !taxItem.isManualTaxAmount);
};

export const getManualTaxItemsForPayment = (
    state: any,
    enableOverridesForSV: boolean = false,
    offerType?: OfferType
): AcceleronTaxItems => {
    const enableOverrideTaxFixes = state.featureToggles['overrideTaxFixes_US1984109'];
    const enablePercentTaxRates = Boolean(state.featureToggles?.['US2131266_Enable_Percent_Tax_Rates']);

    // DE720579 (gated by overrideTaxFixes_US1984109): For Sales View with honorOverrides
    // (enableOverridesForSV) enabled, manual tax AMOUNT overrides (isManualTaxAmount) must be
    // included in the payment/save payload so the override is persisted by Payment Services.
    // Previously these items were filtered out, causing the saved tax amount to be lost on
    // round-trip (reopened deal showed taxAmount: 0 with empty manualChanges).
    const taxesForPayment = getManualTaxItems(state, enableOverrideTaxFixes ? false : enableOverridesForSV, offerType);

    if (enablePercentTaxRates) {
        return taxesForPayment.map((taxItem) => ({ ...taxItem }));
    }

    const items = taxesForPayment.map((taxItem) => ({
        ...taxItem,
        taxRate: taxItem.manualChanges?.taxRate ? Number((taxItem.taxRate! * 100).toFixed(5)) : taxItem.taxRate
    }));
    return items;
};

export const hasManualTaxItems = (state: any, enableOverridesForSV: boolean = false, offerType?: OfferType) => {
    const taxItems = getManualTaxItems(state, enableOverridesForSV, offerType);
    return taxItems.length > 0;
};

export const isTaxResetDisabled = (state: OfferReduxBaseState) => {
    return shouldIncludeTaxes(state) && !hasManualTotalTax(state) && !hasManualTaxItems(state);
};

export const getPaymentTaxItems = (state: Offer): paymentServicesTypes.TaxItems | undefined => {
    return getTaxItems(state.payment);
};

/**
 * Payment tax items that carry a monthly tax amount. Payment tax items arrive with the primary
 * taxes first and additional taxes last, so reversing leads with the additional taxes — the order
 * consumers (e.g. the deal summary monthly payment breakdown) render them in. Reversing is on by
 * default; pass `reverse = false` to keep the original (primary-first) order.
 */
export const getMonthlyPaymentTaxItems = (state: Offer, reverse = true): paymentServicesTypes.TaxItems => {
    const monthlyTaxItems = (getSelectedTerm(state).taxItems ?? []).filter(
        (taxItem: paymentServicesTypes.TaxItem) => taxItem?.monthlyTax != null
    );
    return reverse ? monthlyTaxItems.reverse() : monthlyTaxItems;
};

export function isTaxCapped(state: OfferReduxBaseState, offerType?: OfferType): boolean {
    const selectedTerm = getSelectedTerm(state, offerType);
    return getTaxCapped(selectedTerm) || false;
}

export const isCapCostReductionManual = (state: OfferReduxBaseState): boolean => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state);
    return taxItems.some((taxItem) => taxItem.taxName === TaxNames.CapCostReduction && taxItem.isManualTax);
};

export function getCapCostReductionTax(state: OfferReduxBaseState): number {
    const selectedTerm = getSelectedTerm(state);
    return (selectedTerm?.capCostReductionTax as number) || 0;
}

export const getCapCostReductionTaxItem = (state: OfferReduxBaseState) => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state);
    return taxItems.filter((taxItem) => taxItem.taxName === TaxNames.CapCostReduction);
};

export const getCapCostReductionOverride = (state: OfferReduxBaseState) => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state);
    return taxItems.filter((taxItem) => taxItem.taxName === TaxNames.CapCostReduction && taxItem.isManualTax);
};

export const getTaxItemOverridesWithoutCRT = (state: OfferReduxBaseState) => {
    const taxItems = getTaxItemsWithOverridesWithoutFlat(state);
    return taxItems.filter((taxItem) => taxItem.taxName !== TaxNames.CapCostReduction && taxItem.isManualTax);
};

export function getAdvanceFlatTax(state: OfferReduxBaseState, offerType?: OfferType): number {
    const selectedTerm = getSelectedTerm(state, offerType);
    return (selectedTerm?.advanceFlatTax as number) || 0;
}

export function getAdvanceSalesTax(state: OfferReduxBaseState, offerType?: OfferType): number {
    const selectedTerm = getSelectedTerm(state, offerType);
    return (selectedTerm?.advanceSalesTax as number) || 0;
}

export function getTaxBreakdown(state: OfferReduxBaseState): TaxBreakdown | undefined {
    const offerType = getCurrentOfferType(state);
    return getTaxBreakdownByOfferType(state, offerType);
}

export const getPrimaryTaxBreakdown = (state: OfferReduxBaseState): RegionBreakdown | undefined => {
    const taxBreakdown: TaxBreakdown = getTaxBreakdown(state) || ({} as TaxBreakdown);

    if (!taxBreakdown?.primaryBreakdown) {
        return;
    }

    const primaryBreakdown: RegionBreakdown = taxBreakdown.primaryBreakdown;
    // Filter out keys that include 'Rate' or 'rate' and create a new object with the remaining keys
    const filteredBreakdown: RegionBreakdown = Object.keys(primaryBreakdown)
        .filter((key) => !key.toLowerCase().includes('rate'))
        .reduce((accumulator: RegionBreakdown, key: string) => {
            accumulator[key] = primaryBreakdown[key];
            return accumulator;
        }, {});

    return filteredBreakdown;
};

export function getTaxBreakdownByOfferType(state: OfferReduxBaseState, offerType?: OfferType): TaxBreakdown | undefined {
    const term = getOfferTerm(state, offerType);
    return term?.taxBreakdown;
}

export const getTaxesAndFeesTotal = (state: OfferReduxBaseState, offerType: OfferType) => {
    return getTotalTax(state, offerType) + getTotalFee(state, offerType);
};

export const getAdditionalTaxBreakdown = (state: OfferReduxBaseState) => {
    const taxBreakdown = getTaxBreakdown(state);
    return taxBreakdown?.additionalBreakdown;
};
